import React from 'react';
import { ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import TimeEntryCard from './TimeEntryCard';

interface TimeEntryExtended {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  break_duration: number;
  notes?: string | null;
  hourly_rate?: number | null;
  created_at: string | null;
  updated_at?: string | null;
  project_id: string;
  employee_id: string;
  company_id: string;
  activity_id?: string | null;
  cost_category_id?: string | null;
  entry_type?: string | null;
  is_billable?: boolean | null;
  overtime_hours?: number | null;
  status?: string | null;
  projects?: { name: string; hourly_rate?: number | null } | null;
  employees?: {
    full_name: string;
    hourly_rate: number | null;
  } | null;
  activities?: { name: string } | null;
  cost_categories?: { name: string } | null;
}

interface TimeEntryListProps {
  entries: TimeEntryExtended[];
  onEdit: (entry: TimeEntryExtended) => void;
  onDelete: (entryId: string) => void;
  onDuplicate: (entry: TimeEntryExtended) => void;
  isLoading?: boolean;
  searchTerm?: string;
  onNewEntry?: () => void;
}

const TimeEntryList: React.FC<TimeEntryListProps> = ({
  entries,
  onEdit,
  onDelete,
  onDuplicate,
  isLoading = false,
  searchTerm = '',
  onNewEntry
}) => {
  // Debug logging
  console.log('TimeEntryList rendering:', {
    entriesCount: entries.length,
    isLoading,
    searchTerm,
    sampleEntry: entries[0] ? {
      id: entries[0].id,
      project: entries[0].projects?.name,
      employee: entries[0].employees?.full_name
    } : null
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Entries laden...</p>
      </div>
    );
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
        <ClockIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Geen time entries gevonden
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {searchTerm ? 'Probeer je zoekcriteria aan te passen' : 'Begin door je eerste time entry te maken'}
        </p>
        {onNewEntry && (
          <button
            onClick={onNewEntry}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nieuwe Time Entry
          </button>
        )}
      </div>
    );
  }

  // Entries list
  return (
    <div className="space-y-6">
      {entries.map((entry: TimeEntryExtended) => (
        <TimeEntryCard
          key={entry.id}
          entry={entry}
          onEdit={onEdit}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
        />
      ))}
    </div>
  );
};

export default TimeEntryList;